import React, { useState } from 'react';
import styled from 'styled-components';
import EthNavBar from '../components/eth/EthNavBar';
import EthTokenList from '../components/eth/EthTokenList';
import CreateEthTokenModal from '../components/eth/CreateEthTokenModal';
import EthPriceFooter from '../components/eth/EthPriceFooter';
import { Plus } from 'lucide-react';

// Ethereum Brand Colors
const ETH_BLUE = '#627EEA';
const ETH_DARK = '#333333';
const ETH_LIGHT = '#FFFFFF';
const ETH_BLACK = '#000000';

const PageContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  background: ${ETH_LIGHT};
  color: ${ETH_DARK};
  font-family: 'Inter', sans-serif;
  display: flex;
  flex-direction: column;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-bottom: 60px; /* Space for footer */
`;

const HeroSection = styled.div`
  padding: 60px 24px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: linear-gradient(180deg, rgba(98, 126, 234, 0.05) 0%, rgba(255, 255, 255, 0) 100%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
`;

const HeroTitle = styled.h1`
  font-size: 48px;
  font-weight: 800;
  color: ${ETH_DARK};
  margin: 0 0 16px 0;
  letter-spacing: -1px;
  
  span {
    color: ${ETH_BLUE};
  }
  
  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 18px;
  color: #666666;
  max-width: 600px;
  margin: 0 0 32px 0;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ActionButton = styled.button`
  background: ${ETH_BLUE};
  color: #FFFFFF;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(98, 126, 234, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(98, 126, 234, 0.4);
    background: #5370E0;
  }
`;

const InfoBox = styled.div`
  background: rgba(98, 126, 234, 0.08);
  border: 1px solid rgba(98, 126, 234, 0.2);
  border-radius: 8px;
  padding: 16px 20px;
  margin: 0 0 24px 0;
  max-width: 700px;
  text-align: left;
  
  strong {
    color: ${ETH_BLUE};
    font-weight: 600;
  }
  
  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.6;
    color: #555555;
  }
`;

const FAQSection = styled.div`
  max-width: 700px;
  margin: 0 0 32px 0;
  text-align: left;
`;

const FAQTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: ${ETH_DARK};
  margin: 0 0 16px 0;
  text-align: center;
`;

const FAQItem = styled.div`
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  margin-bottom: 8px;
  overflow: hidden;
  background: #FFFFFF;
  transition: all 0.2s;
  
  &:hover {
    border-color: ${ETH_BLUE};
  }
`;

const Question = styled.button`
  width: 100%;
  background: transparent;
  border: none;
  padding: 14px 18px;
  text-align: left;
  font-size: 15px;
  font-weight: 600;
  color: ${ETH_DARK};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s;
  
  &:hover {
    color: ${ETH_BLUE};
  }
  
  span {
    transition: transform 0.2s;
  }
`;

const Answer = styled.div<{ $isOpen: boolean }>`
  max-height: ${props => props.$isOpen ? '500px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
  
  p {
    padding: 0 18px 14px 18px;
    margin: 0;
    font-size: 14px;
    line-height: 1.6;
    color: #666666;
  }
`;

const EthPage: React.FC = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  // Force white background for Ethereum page
  React.useEffect(() => {
    // Save original styles
    const originalBackground = document.body.style.background;
    const originalColor = document.body.style.color;

    // Apply Ethereum styles
    document.body.style.background = '#FFFFFF';
    document.body.style.color = '#333333';

    return () => {
      // Restore original styles
      document.body.style.background = originalBackground;
      document.body.style.color = originalColor;
    };
  }, []);

  const handleCreateSuccess = () => {
    // Refresh logic handled by list component or context
    window.location.reload();
  };

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const faqs = [
    {
      question: "How is LP locked?",
      answer: "The LP NFT remains in the Factory contract where it cannot be withdrawn. Creators can collect fees from the contract by calling the collectFees function."
    },
    {
      question: "How much do creators earn?",
      answer: "Every token deployed in the Factory contract has a 1% LP trading fee. Creators earn 75% of the total trading fees."
    },
    {
      question: "Do token contracts need to be renounced?",
      answer: "Every new token created is automatically renounced at launch. The owner address is set to the Ethereum dEaD address at the time of deployment."
    },
    {
      question: "How does the token have liquidity at launch?",
      answer: "We treat liquidity as a computational output, not a capital input. By instructing the pool with strict parameters, we are able to simulate liquidity depth at launch. Every trade occurs within a safe and tested Uniswap concentrated liquidity market maker formula."
    }
  ];

  return (
    <PageContainer>
      <EthNavBar />

      <MainContent>
        <HeroSection>
          <HeroTitle>
            Build on <span>Ethereum</span>
          </HeroTitle>
          <HeroSubtitle>
            Deploy tokens on the world's leading programmable blockchain.
          </HeroSubtitle>

          <InfoBox>
            <p>
              <strong>💡 Gas Tip:</strong> When deploying, change your gas settings to advanced and use a <strong>0.5 GWEI - 1.0 GWEI</strong> max base fee and max priority fee for optimal transaction costs.
            </p>
          </InfoBox>

          <FAQSection>
            <FAQTitle>Frequently Asked Questions</FAQTitle>
            {faqs.map((faq, index) => (
              <FAQItem key={index}>
                <Question onClick={() => toggleFAQ(index)}>
                  {faq.question}
                  <span style={{ transform: openFAQ === index ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                </Question>
                <Answer $isOpen={openFAQ === index}>
                  <p>{faq.answer}</p>
                </Answer>
              </FAQItem>
            ))}
          </FAQSection>

          <ActionButton onClick={() => setIsCreateModalOpen(true)}>
            <Plus size={20} />
            Deploy Token
          </ActionButton>
        </HeroSection>

        <EthTokenList searchQuery={searchQuery} onSearchChange={setSearchQuery} />
      </MainContent>

      <CreateEthTokenModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      <EthPriceFooter />
    </PageContainer>
  );
};

export default EthPage;
